import { Search, X } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Badge } from '~/components/ui/badge';
import { Button } from '~/components/ui/button';
import { Input } from '~/components/ui/input';
import { Label } from '~/components/ui/label';
import { Switch } from '~/components/ui/switch';
import { Tooltip, TooltipContent, TooltipTrigger } from '~/components/ui/tooltip';
import { cn } from '~/utils/cn';

import type {
  ExportFieldPreset,
  ExportTemplateColumn,
  ExportTemplateDefinition,
} from '../types/export.types';
import { applyDescriptorChoice } from '../utils/export-column-selection';

export interface ExportColumnListProps {
  columns: ExportTemplateColumn[];
  /** The draft selection this list edits. Owned by the container. */
  draft: Set<string>;
  onDraftChange: (draft: Set<string>) => void;
  /**
   * The active template, for the descriptor either/or.
   *
   * Optional because the browse column drawer has no template to offer — it
   * lists rows, not a feed export — and with none, `absorbsDescriptors` is
   * false and every column toggles independently, exactly as before.
   */
  // `| undefined` explicitly: `exactOptionalPropertyTypes` is on, so `?`
  // alone would allow an absent prop but reject one passed as undefined —
  // which is what the picker forwards when no template is selected.
  template?: ExportTemplateDefinition | undefined;
  /** The run's enriched attributes, for the "Run attributes" reset and grouping. */
  runFields: readonly string[] | undefined;
  /** Run attributes with no matching connector column — surfaced, not swallowed. */
  unmatched: string[];
  presets: ExportFieldPreset[];
  /** Tailwind height for the scrolling list, set by the container. */
  listClassName?: string;
}

/** One section of the list. Order here is the order on screen. */
interface ColumnGroup {
  id: 'identifiers' | 'run' | 'other';
  columns: ExportTemplateColumn[];
}

/**
 * The column list itself: search, the quick resets, and one toggle row per
 * column — the same row anatomy as the app's other Manage Columns drawers
 * (label, a one-line description, a switch), so choosing export columns looks
 * like choosing table columns anywhere else.
 *
 * Grouped into what a merchandiser is actually deciding between: the
 * identifiers a feed needs, the attributes this run enriched (the default, and
 * the columns with data in them), and everything else the connector offers.
 * Every content row toggles. Identifiers are locked when the template
 * publishes them: the server writes them whatever `fields` says, so a switch
 * for one did nothing — an unticked `id` still landed in the file.
 *
 * Extracted because it needs two containers and the list is the same in both —
 * a popover inside the export drawer and a sheet on the full-page view.
 * Draft-owned-by-the-container is what lets both apply on commit rather than
 * per tick: every change here costs a preview re-render or a page read.
 */
export function ExportColumnList({
  columns,
  draft,
  onDraftChange,
  template,
  runFields,
  unmatched,
  presets,
  listClassName,
}: ExportColumnListProps) {
  const { t } = useTranslation('exports');
  const [query, setQuery] = useState('');

  const groups = useMemo<ColumnGroup[]>(() => {
    const needle = query.trim().toLowerCase();
    const matches = (column: ExportTemplateColumn) =>
      !needle ||
      column.label.toLowerCase().includes(needle) ||
      column.key.toLowerCase().includes(needle);
    const run = new Set(runFields ?? []);
    const buckets: Record<ColumnGroup['id'], ExportTemplateColumn[]> = {
      identifiers: [],
      run: [],
      other: [],
    };
    for (const column of columns) {
      if (!matches(column)) continue;
      // Rendered from the template's own list instead (`lockedIdentifiers`).
      if (column.identifier && template?.identifiers) continue;
      if (column.identifier) buckets.identifiers.push(column);
      else if (run.has(column.key)) buckets.run.push(column);
      else buckets.other.push(column);
    }
    return (['identifiers', 'run', 'other'] as const)
      .map((id) => ({ id, columns: buckets[id] }))
      .filter((group) => group.columns.length > 0);
  }, [columns, query, runFields, template]);

  /** The template's identifier columns, shown as always included. Labelled
   *  from the connector's column of that key when it declares one, else by
   *  the key itself (`gmc-feed`'s `offer_id`). */
  const lockedIdentifiers = useMemo(() => {
    if (!template?.identifiers) return [];
    const needle = query.trim().toLowerCase();
    const byKey = new Map(columns.map((column) => [column.key, column]));
    return template.identifiers
      .map((key) => ({ key, label: byKey.get(key)?.label ?? key }))
      .filter(
        ({ key, label }) =>
          !needle || label.toLowerCase().includes(needle) || key.toLowerCase().includes(needle),
      );
  }, [columns, query, template]);

  /** Replaces the whole draft — what the resets and the presets commit. No
   *  column is forced, so a preset says exactly what the file will contain. */
  const setTo = (keys: Iterable<string>) => {
    onDraftChange(new Set(keys));
  };

  const toggle = (key: string) => {
    // Delegated so the descriptor either/or lives with the rest of the
    // selection rules rather than in a component: on a template that folds a
    // descriptor into `description`, checking one unchecks the other.
    // Unchecking leaves neither, which is a real choice — `description` then
    // exports on its own.
    onDraftChange(applyDescriptorChoice({ draft, key, checked: !draft.has(key), template }));
  };

  return (
    <div data-testid="export-column-list" className="flex min-h-0 flex-1 flex-col">
      <div className="relative shrink-0 px-4 py-3">
        <Search
          aria-hidden="true"
          className="text-muted-foreground pointer-events-none absolute top-1/2 left-7 size-4 -translate-y-1/2"
        />
        <Input
          value={query}
          onChange={(event) => {
            setQuery(event.target.value);
          }}
          placeholder={t('estimate.columns.searchPlaceholder')}
          aria-label={t('estimate.columns.searchPlaceholder')}
          data-testid="export-column-search-input"
          className="h-9 pr-8 pl-9 text-sm"
        />
        {query !== '' && (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="absolute top-1/2 right-5 h-7 w-7 -translate-y-1/2 cursor-pointer"
            aria-label={t('browse.clearSearch')}
            data-testid="export-column-search-clear-button"
            onClick={() => {
              setQuery('');
            }}
          >
            <X aria-hidden="true" className="size-3.5" />
          </Button>
        )}
      </div>

      {/* Quick actions as a row of links, right-aligned under the search: they
          act on the whole list, so they read as list controls rather than as a
          second set of buttons competing with the footer. */}
      <div className="flex shrink-0 flex-wrap items-center justify-end gap-x-1 border-b px-4 pb-2">
        {unmatched.length > 0 && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Badge
                variant="outline"
                data-testid="export-column-unmatched-badge"
                className="text-warning border-warning/40 mr-auto cursor-help text-[11px] font-normal"
              >
                {t('estimate.columns.unmatched', { count: unmatched.length })}
              </Badge>
            </TooltipTrigger>
            <TooltipContent className="max-w-xs">
              {t('estimate.columns.unmatchedHint', { keys: unmatched.join(', ') })}
            </TooltipContent>
          </Tooltip>
        )}
        {/* The run's own attributes lead: it is the default, and the state an
            operator returns to after exploring. */}
        {runFields && runFields.length > 0 && (
          <Button
            type="button"
            variant="link"
            size="sm"
            className="h-7 cursor-pointer px-1.5 text-xs"
            data-testid="export-column-reset-run-button"
            onClick={() => {
              setTo(runFields);
            }}
          >
            {t('columns.resetToRun', { count: runFields.length })}
          </Button>
        )}
        {presets.map((preset) => (
          <Tooltip key={preset.code}>
            <TooltipTrigger asChild>
              <Button
                type="button"
                variant="link"
                size="sm"
                className="h-7 cursor-pointer px-1.5 text-xs"
                data-testid={`export-column-preset-${preset.code}-button`}
                onClick={() => {
                  setTo(preset.fields);
                }}
              >
                {preset.label}
              </Button>
            </TooltipTrigger>
            <TooltipContent className="max-w-xs">{preset.description}</TooltipContent>
          </Tooltip>
        ))}
        <Button
          type="button"
          variant="link"
          size="sm"
          className="h-7 cursor-pointer px-1.5 text-xs"
          data-testid="export-column-select-all-button"
          onClick={() => {
            setTo(columns.map((column) => column.key));
          }}
        >
          {t('estimate.columns.selectAll')}
        </Button>
        <span aria-hidden="true" className="text-muted-foreground text-xs">
          ·
        </span>
        <Button
          type="button"
          variant="link"
          size="sm"
          className="h-7 cursor-pointer px-1.5 text-xs"
          data-testid="export-column-clear-button"
          onClick={() => {
            setTo([]);
          }}
        >
          {t('estimate.columns.clear')}
        </Button>
      </div>

      <div className={cn('min-h-0 flex-1 overflow-y-auto', listClassName)}>
        {lockedIdentifiers.length > 0 && (
          <div data-testid="export-column-group-locked">
            <div className="text-muted-foreground bg-muted/30 px-6 py-2 text-xs font-medium">
              {t('columns.group.identifiers')}
            </div>
            {lockedIdentifiers.map(({ key, label }) => (
              <div
                key={key}
                data-testid={`export-column-locked-${key}`}
                className="flex items-center justify-between border-b px-6 py-3"
              >
                <div className="min-w-0 pr-3">
                  <p className="truncate text-sm font-medium">{label}</p>
                  <p className="text-muted-foreground mt-0.5 truncate font-mono text-xs">{key}</p>
                </div>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Badge
                      variant="secondary"
                      className="shrink-0 cursor-help text-[11px] font-normal"
                    >
                      {t('columns.alwaysIncluded')}
                    </Badge>
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs">
                    {t('columns.alwaysIncludedHint')}
                  </TooltipContent>
                </Tooltip>
              </div>
            ))}
          </div>
        )}
        {groups.length === 0 && lockedIdentifiers.length === 0 ? (
          <p
            data-testid="export-column-list-empty"
            className="text-muted-foreground px-6 py-10 text-center text-sm"
          >
            {t('estimate.columns.noMatches')}
          </p>
        ) : (
          groups.map((group) => (
            <div key={group.id} data-testid={`export-column-group-${group.id}`}>
              <div className="text-muted-foreground bg-muted/30 px-6 py-2 text-xs font-medium">
                {t(`columns.group.${group.id}`)}
              </div>
              {group.columns.map((column) => {
                const switchId = `export-column-${column.key}`;
                return (
                  <div
                    key={column.key}
                    data-testid={`export-column-row-${column.key}`}
                    className="flex items-center justify-between border-b px-6 py-3"
                  >
                    <div className="min-w-0 pr-3">
                      <Label
                        htmlFor={switchId}
                        className="block cursor-pointer truncate text-sm font-medium"
                      >
                        {column.label}
                      </Label>
                      <p className="text-muted-foreground mt-0.5 truncate font-mono text-xs">
                        {column.key}
                      </p>
                    </div>
                    <Switch
                      id={switchId}
                      checked={draft.has(column.key)}
                      data-testid={`export-column-toggle-${column.key}`}
                      onCheckedChange={() => {
                        toggle(column.key);
                      }}
                      className="cursor-pointer"
                    />
                  </div>
                );
              })}
            </div>
          ))
        )}
      </div>
    </div>
  );
}
